import { Button, ButtonGroup } from "@chakra-ui/react"
import { MdChevronLeft, MdChevronRight } from "react-icons/md"

interface PaginationProps {
  totalPages: number
  currentPage: number
  activeDivisor?: boolean
  onPageChange: (page: number) => void
  disableDivider?: boolean
}

export default function Pagination({ totalPages, currentPage, onPageChange, disableDivider, activeDivisor = true}: PaginationProps) {
  const safeTotalPages = typeof totalPages === 'number' && totalPages > 0 ? totalPages : 1;
  const safeCurrentPage = typeof currentPage === 'number' && currentPage > 0 ? currentPage : 1;

  const getVisiblePages = (totalPages: number, currentPage: number, showMax: number): number[] => {
    const paginationNumbers = []

    let startPage: number
    let endPage: number

    if (totalPages <= showMax) {
      startPage = 1
      endPage = totalPages
    } else {
      startPage = Math.max(currentPage - 1, 1)
      endPage = Math.min(currentPage + 1, totalPages)

      if (endPage - startPage + 1 < showMax) {
        if (currentPage < totalPages / 2) {
          endPage = Math.min(startPage + showMax - 1, totalPages)
        } else {
          startPage = Math.max(endPage - showMax + 1, 1)
        }
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationNumbers.push(i)
    }

    return paginationNumbers
  }

  const visiblePages = getVisiblePages(safeTotalPages, safeCurrentPage, 5)

  const handlePrev = () => {
    if (safeCurrentPage > 1) {
      onPageChange(safeCurrentPage - 1)
    }
  }

  const handleNext = () => {
    if (safeCurrentPage < safeTotalPages) {
      onPageChange(safeCurrentPage + 1)
    }
  }

  return (
    <ButtonGroup
      padding={2}
      width={'full'}
      size={'sm'}
      bottom={0}
      borderRadius={'0 0 16px 16px'}
      zIndex={3}
      alignItems={'center'}
      role='tablist'
      borderTop={(activeDivisor || !disableDivider) ?'1px solid #ccc': undefined}
      justifyContent={'center'}
    >
      <Button p={1} role='tab' onClick={handlePrev} disabled={safeCurrentPage === 1}>
        <MdChevronLeft size={30} />
      </Button>

      {visiblePages.map((pageNumber) => (
        <Button
          key={pageNumber}
          fontSize={15}
          colorScheme={safeCurrentPage === pageNumber ? 'blue' : 'gray'}
          role='tab'
          onClick={() => onPageChange(pageNumber)}
        >
          {pageNumber}
        </Button>
      ))}

      <Button p={1} role='tab' onClick={handleNext} disabled={safeCurrentPage === safeTotalPages}>
        <MdChevronRight size={30} />
      </Button>
    </ButtonGroup>
  )
}

